import React, { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import appConfig from '../../../config/appConfig';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  buildQualifiedPath,
  filterPathEntries,
  flattenCatalogTree,
  getMappingStatus,
  normalizeFieldCatalog,
  parseRawConfigFromText,
} from '../utils/fieldCatalogUtils';

const copyPath = async (path) => {
  try {
    await navigator.clipboard.writeText(path);
  } catch {
    // Clipboard may be unavailable in non-secure contexts.
  }
};

const PathRow = ({ path, required, kind, mappingStatus, onCopy }) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="space-between"
    spacing={1}
    sx={{
      py: 0.75,
      borderBottom: '1px solid',
      borderColor: 'divider',
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
      {kind !== 'field' && <Chip label={kind} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />}
      <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
        {path}
      </Typography>
    </Stack>
    <Stack direction="row" spacing={0.5} alignItems="center" flexShrink={0}>
      {required && <Chip label="Required" size="small" color="warning" />}
      {mappingStatus === 'configured' ? (
        <Chip label="Mapped" size="small" color="success" />
      ) : (
        <Chip label="Not mapped" size="small" />
      )}
      <Tooltip title="Copy qualified path">
        <IconButton size="small" onClick={() => onCopy(path)} aria-label={`Copy ${path}`}>
          <ContentCopyIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
    </Stack>
  </Stack>
);

const CatalogTreeSection = ({ name, node, parentPath, rawConfig, searchQuery, defaultExpanded = false }) => {
  const path = buildQualifiedPath(parentPath, name);
  const childEntries = flattenCatalogTree({ [name]: node }, parentPath).filter((entry) => entry.path !== path);
  const filteredChildren = filterPathEntries(childEntries, searchQuery);
  const mappingStatus = getMappingStatus(path, rawConfig);
  const matchesSelf =
    !searchQuery.trim() ||
    path.toLowerCase().includes(searchQuery.trim().toLowerCase()) ||
    node.kind.toLowerCase().includes(searchQuery.trim().toLowerCase());

  if (node.kind === 'field') {
    if (!matchesSelf && filteredChildren.length === 0) {
      return null;
    }

    return (
      <PathRow path={path} required={node.required} kind={node.kind} mappingStatus={mappingStatus} onCopy={copyPath} />
    );
  }

  if (!matchesSelf && filteredChildren.length === 0) {
    return null;
  }

  return (
    <Accordion defaultExpanded={defaultExpanded || mappingStatus === 'configured'} disableGutters sx={{ mb: 1 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip label={node.kind} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            {path}
          </Typography>
          {node.required && <Chip label="Required" size="small" color="warning" />}
          {mappingStatus === 'configured' ? (
            <Chip label="Mapped" size="small" color="success" />
          ) : (
            <Chip label="Not mapped" size="small" />
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        {Object.entries(node.children || {}).map(([childName, childNode]) => (
          <CatalogTreeSection
            key={buildQualifiedPath(path, childName)}
            name={childName}
            node={childNode}
            parentPath={path}
            rawConfig={rawConfig}
            searchQuery={searchQuery}
          />
        ))}
      </AccordionDetails>
    </Accordion>
  );
};

const MarminFieldReferencePanel = ({ fieldCatalog, configJsonText }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expanded, setExpanded] = useState(true);
  const normalizedCatalog = useMemo(() => normalizeFieldCatalog(fieldCatalog), [fieldCatalog]);
  const rawConfig = useMemo(() => parseRawConfigFromText(configJsonText), [configJsonText]);
  const flatEntries = useMemo(
    () => filterPathEntries(normalizedCatalog.flatEntries, searchQuery),
    [normalizedCatalog.flatEntries, searchQuery],
  );

  if (!fieldCatalog) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        Loading {appConfig.brandName} field catalog…
      </Alert>
    );
  }

  return (
    <Accordion expanded={expanded} onChange={(_, isExpanded) => setExpanded(isExpanded)} sx={{ mt: 2 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle1" fontWeight={600}>
          {appConfig.brandName} field reference
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Browse the request-mappable field tree. Copy qualified paths like <code>document_lines.charges.amount</code>{' '}
          when authoring nested <code>fields</code> JSON. Mapped status reflects the current JSON editor content.
        </Typography>

        <TextField
          fullWidth
          size="small"
          label="Filter fields"
          placeholder={`Search ${appConfig.brandName} paths…`}
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          sx={{ mb: 2 }}
        />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          All paths ({flatEntries.length})
        </Typography>

        {Object.entries(normalizedCatalog.tree).map(([name, node]) => (
          <CatalogTreeSection
            key={name}
            name={name}
            node={node}
            parentPath=""
            rawConfig={rawConfig}
            searchQuery={searchQuery}
            defaultExpanded={node.required}
          />
        ))}
      </AccordionDetails>
    </Accordion>
  );
};

export default MarminFieldReferencePanel;
